import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';
import './UI.css';

const UIContext = createContext();

export const useUI = () => useContext(UIContext);

export const UIProvider = ({ children }) => {
    // Toast State
    const [toasts, setToasts] = useState([]);

    // Modal States
    const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', resolve: null });
    const [promptState, setPromptState] = useState({ isOpen: false, title: '', message: '', defaultValue: '', resolve: null });

    // Toast Logic
    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    }, []);

    const removeToast = (id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    // Confirm Logic
    const confirm = useCallback((message, title = 'Confirm Action') => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                title,
                message,
                resolve
            });
        });
    }, []);

    const handleConfirm = (result) => {
        if (confirmState.resolve) confirmState.resolve(result);
        setConfirmState({ ...confirmState, isOpen: false });
    };

    // Prompt Logic
    const prompt = useCallback((message, defaultValue = '') => {
        return new Promise((resolve) => {
            setPromptState({
                isOpen: true,
                title: 'Input Required',
                message,
                defaultValue,
                resolve
            });
        });
    }, []);

    const handlePrompt = (value) => {
        if (promptState.resolve) promptState.resolve(value);
        setPromptState({ ...promptState, isOpen: false });
    };

    // Offline State
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    React.useEffect(() => {
        const handleOnline = () => {
            if (!isOffline) return;
            setIsOffline(false);
            showToast('You are now online', 'success');
        };
        const handleOffline = () => {
            if (isOffline) return;
            setIsOffline(true);
            showToast('You are now offline', 'error');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('offline-mode', handleOffline); // Listen for custom event

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('offline-mode', handleOffline);
        };
    }, [isOffline, showToast]);

    return (
        <UIContext.Provider value={{ showToast, confirm, prompt, isOffline }}>
            {children}

            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <div key={toast.id} className={`toast toast-${toast.type}`}>
                        <div className="toast-icon">
                            {toast.type === 'success' && <FiCheckCircle />}
                            {toast.type === 'error' && <FiAlertCircle />}
                            {toast.type === 'info' && <FiInfo />}
                        </div>
                        <span className="toast-message">{toast.message}</span>
                        <button className="toast-close" onClick={() => removeToast(toast.id)}>
                            <FiX />
                        </button>
                    </div>
                ))}
            </div>

            {/* Confirmation Modal */}
            {confirmState.isOpen && (
                <div className="modal-overlay ui-modal-overlay">
                    <div className="modal ui-modal">
                        <div className="modal-header">
                            <h3>{confirmState.title}</h3>
                        </div>
                        <div className="modal-body">
                            <p>{confirmState.message}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => handleConfirm(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={() => handleConfirm(true)}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Prompt Modal */}
            {promptState.isOpen && (
                <PromptModal
                    state={promptState}
                    onClose={handlePrompt}
                />
            )}
        </UIContext.Provider>
    );
};

// Extracted Prompt Modal to handle input state
const PromptModal = ({ state, onClose }) => {
    const [value, setValue] = useState(state.defaultValue);
    const inputRef = useRef(null);

    React.useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        onClose(value);
    };

    return (
        <div className="modal-overlay ui-modal-overlay">
            <div className="modal ui-modal">
                <div className="modal-header">
                    <h3>{state.title}</h3>
                    <button className="modal-close" onClick={() => onClose(null)}><FiX /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <label className="form-label">{state.message}</label>
                        <input
                            ref={inputRef}
                            className="form-input"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => onClose(null)}>Cancel</button>
                        <button type="submit" className="btn btn-primary">OK</button>
                    </div>
                </form>
            </div>
        </div>
    );
};
